import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { LaunchpoolStatus } from '@flipster/apis/v1/launchpool/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { launchpoolItemBunja, useLaunchpool } from '@flipster/features/src/shared/launchpool/state';
import { useOverlay } from '@flipster/overlay';
import { Button, TextButton } from '@flipster/react/design-system';
import { snackbar } from '@flipster/react/design-system/snackbar';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { alert } from '@flipster/utils/alert';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { dividerCssRaw } from '@web/app/[locale]/(web)/launchpool/_lists/_Item/shared-css';
import ConvertInfoModal from '@web/app/[locale]/(web)/launchpool/_modals/ConvertInfoModal';

const BottomSection = () => {
  const router = useRouter();
  const overlay = useOverlay();
  const { isLoggedIn } = useAuth();
  const { launchpoolList, postLaunchpoolAutoConversionMutation } = useLaunchpool();
  const { launchpoolItemAtom } = useBunja(launchpoolItemBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);

  const launchpool = useAtomValue(launchpoolItemAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const {
    id,
    stakingCurrency,
    stakedAmount,
    status,
    autoConversionOn: isAutoConversionOn,
    autoConversionSupported,
    autoConversionTooltip,
  } = launchpool;

  const isClosed = launchpool.status === LaunchpoolStatus.Closed;
  const usdtAppliedApr = getEarnSummaryOfCurrency(UsdtCurrency, earnSummary).appliedAprPct;

  const postAutoConvertEarn = async () => {
    try {
      await postLaunchpoolAutoConversionMutation.mutateAsync({
        launchpoolId: id,
        data: { turnOn: !isAutoConversionOn },
      });
      if (isAutoConversionOn) {
        snackbar.success({ message: 'Earn setup has been cancelled' });
      } else {
        alert.show({
          title: (
            <div
              className={flex({
                align: { base: 'flex-start', smallDesktop: 'center' },
                textStyle: 'Title/S',
                color: 'content.default.level-4',
                gap: 4,
              })}
            >
              <CheckCircleIcon size={24} color={token('colors.content.accent.info')} />
              <span>Auto convert is now active</span>
            </div>
          ),
          message: (
            <span className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
              {
                'Your staked assets will automatically convert to USDT and will be invested in Flipster Earn after the Launchpool ends.\n\nAPR will be applied after conversion (may take a few minutes).\n\nNote: Auto convert may be canceled if the 24-hours conversion limit is exceeded.'
              }
            </span>
          ),
          confirmText: 'Done',
        });
      }
      segment.track(StakeEvent.StakeAutoConvertClick, {
        currency: stakingCurrency,
        status,
        set_on: !isAutoConversionOn,
        event_location: 'earn_launchpool_tab',
      });
    } catch (error) {
      alert.error(error);
    }
  };

  const handleClickAutoConvertEarn = async () => {
    if (!isLoggedIn) {
      router.push('/signin');
      return;
    }

    if (isAutoConversionOn) {
      alert.show({
        title: 'Turn off auto convert',
        message: `If you turn off Auto convert, your funds will not be moved to Flipster Earn for ${usdtAppliedApr.toString()}% APR. Would you like to proceed?`,
        closeText: 'Cancel',
        confirmText: 'Turn off',
        onConfirm: postAutoConvertEarn,
        withClose: true,
      });
      return;
    }
    await postAutoConvertEarn();
  };

  const isUSDT = stakingCurrency === UsdtCurrency;
  const isShowSetAutoEarn = autoConversionSupported && !(isClosed || isUSDT) && !launchpoolList?.isAbuserOrInternal;
  const isUserStaked = Number(stakedAmount) !== 0;
  const isHideBottomSection = isClosed && !isUserStaked;

  if (isHideBottomSection) return null;

  const handleClickModal = () => {
    overlay.open((close) => <ConvertInfoModal launchpoolId={id} onClose={close} />);
  };

  if (isShowSetAutoEarn)
    return (
      <>
        <div className={css(dividerCssRaw, { mt: 16, mb: 12 })} />
        <div className={hstack({ textStyle: 'Text/M/Regular', justify: 'center', gap: 20 })}>
          <div className={hstack({ gap: 4, flexWrap: 'wrap', tabletDown: { flex: 1 } })}>
            <span>{autoConversionTooltip.title}</span>
            <span className={css({ color: 'content.state.positive' })}>{autoConversionTooltip.highlightedTitle}</span>
            <button type='button' onClick={handleClickModal} className={css({ buttonShape: 'inline-icon' })}>
              <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
            </button>
          </div>
          {isAutoConversionOn && (
            <TextButton
              color='passive'
              size='medium'
              disabled={postLaunchpoolAutoConversionMutation.isPending}
              onClick={handleClickAutoConvertEarn}
              label='Turn off'
            />
          )}
          {!isAutoConversionOn && (
            <Button
              hierarchy='primary'
              color='neutral'
              size='small'
              isLoading={postLaunchpoolAutoConversionMutation.isPending}
              onClick={handleClickAutoConvertEarn}
              disabled={!isUserStaked}
              label='Turn on'
            />
          )}
        </div>
      </>
    );

  if (isClosed && isAutoConversionOn)
    return (
      <>
        <div className={css(dividerCssRaw, { mt: 16, mb: 12 })} />
        <div
          className={css({
            display: 'flex',
            textStyle: 'Text/M/Regular',
            color: 'content.default.level-4',
            justifyContent: 'center',
            alignItems: { base: 'flex-start', tablet: 'center' },
            flexDirection: { base: 'column', tablet: 'row' },
          })}
        >
          <span className={css({ mr: 8 })}>Your staked asset is earning extra USDT.</span>
          <TextButton
            as='a'
            size='medium'
            color='neutral'
            href='/earn'
            onClick={() => segment.track(StakeEvent.StakeGoToEarnClick, { button_text: 'Go to Earn' })}
            label='Go to Earn'
          />
        </div>
      </>
    );

  return null;
};

export default BottomSection;
